import React, { Children } from 'react';

import delve from 'dlv';
import styled from 'styled-components';

import { OnClick } from '@rbi-ctg/frontend';
import {
  IModifierSelection,
  IPickerItem,
  IQuickConfig,
  ISanityItemOption,
  ISanityItemOptionModifier,
} from '@rbi-ctg/menu';
import { logProductModifierCustomization } from 'components/product-detail/modifier/utils';
import { IMParticleCtx, useMParticleContext } from 'state/mParticle';
import { IUIRule, rulesForItem } from 'utils/menu/quick-configs';

import { QuickConfigsList } from './quick-config-list';

interface IQuickConfigItemProps {
  modifierSelections: IModifierSelection[];
  onClick: OnClick<HTMLDivElement>;
  rules: IUIRule[];
}

const isActive = ({ modifierSelections, rules = [] }: IQuickConfigItemProps): boolean => {
  return (
    !!rules.length &&
    rules.every(({ itemOption: ruleItemOpt, modifier }) => {
      const itemOption = modifierSelections.find(({ _key }) => ruleItemOpt._key === _key);
      return !!itemOption && itemOption.modifier._key === modifier._key;
    })
  );
};

const QuickConfigItem = styled.div<IQuickConfigItemProps>`
  align-items: center;
  display: flex;
  font-size: 0.975rem;
  height: 100%;
  justify-content: center;
  width: 100%;
  padding: 4px;
  text-align: center;
  ${props =>
    isActive(props)
      ? `
    background-color: ${Styles.color.primary};
    color: ${Styles.color.white};
    `
      : `
    color: ${Styles.color.black};
  `}
`;

type HandleModifierSelection = (
  id: string,
  modifier: ISanityItemOptionModifier,
  itemOption: ISanityItemOption
) => void;

interface IQuickConfigsProps {
  modifierSelections: IModifierSelection[];
  handleModifierSelection: HandleModifierSelection;
  pickerSelection: IPickerItem;
  quickConfigs: IQuickConfig[];
}

const onClickQuickConfig =
  (
    rules: IUIRule[],
    item: IPickerItem,
    handleModifierSelection: HandleModifierSelection,
    mParticle: IMParticleCtx
  ) =>
  () => {
    rules.forEach(({ itemOption, modifier }) => {
      const itemOpt = item.options.find(({ _key }) => itemOption._key === _key);
      if (itemOpt) {
        logProductModifierCustomization(
          modifier?.name.locale ?? '',
          itemOption.name.locale,
          modifier?._key ?? '',
          mParticle
        );
        return handleModifierSelection(item._id, modifier, itemOpt);
      }
    });
  };

const QuickConfigs = ({
  handleModifierSelection,
  modifierSelections,
  pickerSelection = {} as IPickerItem,
  quickConfigs = [],
}: IQuickConfigsProps) => {
  const mParticle = useMParticleContext();
  if (quickConfigs.length === 0) {
    return null;
  }
  const items = quickConfigs.reduce<React.ReactNode[]>((acc, qc, i) => {
    if (!qc.rules) {
      return acc;
    }

    const rules = rulesForItem(qc.rules, pickerSelection);

    const configName: string = delve(qc, 'name.locale') || '';

    // if a rule is not present that means 1 or more
    // modifiers are not available for this item,
    // so we cannot render this quick config
    return rules.length !== qc.rules.length
      ? acc
      : acc.concat(
          <QuickConfigItem
            key={configName.concat(String(i))}
            modifierSelections={modifierSelections}
            onClick={onClickQuickConfig(rules, pickerSelection, handleModifierSelection, mParticle)}
            rules={rules}
          >
            {configName}
          </QuickConfigItem>
        );
  }, []);

  if (Children.count(items) === 0) {
    return null;
  }

  return <QuickConfigsList>{items}</QuickConfigsList>;
};

export default QuickConfigs;
